/**
 * Generic helpers that surface a device's `Serial` component (`serial:N`)
 * as an addon peripheral entry.
 *
 */

//Hooks
import { get } from 'svelte/store';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
//Formatters
import { format } from 'svelte-i18n';
//Helpers
import { getSerialComponent, real_id } from '../interface_helpers';
//Stores
import { devices_status } from 'src/stores/device-runtime/status';
//Types
import type { Device } from 'src/types/Device';
import type { Peripheral, PeripheralSettings, Sensor } from 'src/types/Peripherals';
import type { SerialConfig, SerialMode } from 'src/types/SerialConfig';

const SERIAL_ICON = 'fal fa-arrow-down-arrow-up';

/**
 * Catalog of friendly labels per known `serial.mode` value.
 *
 * - `js_uart` is the canonical UART value reported by both Pill and other
 *   devices with Serial support.
 * - `mb_client` / `mb_server` are the two Modbus RTU variants;
 *
 */
export const SERIAL_MODE_LABELS: Record<string, { key: string; default: string }> = {
	js_uart: { key: 'label_serial_mode_uart', default: 'UART' },
	mb_client: { key: 'label_serial_mode_modbus_rtu_client', default: 'Modbus RTU Client' },
	mb_server: { key: 'label_serial_mode_modbus_rtu_server', default: 'Modbus RTU Server' },
};

/**
 * Resolve the translated display label for a `serial.mode` value.
 * Falls back to the raw string when the value is not in the catalog.
 */
export const getSerialModeLabel = (mode: SerialMode | undefined): string => {
	if (!mode) return '';
	const entry = SERIAL_MODE_LABELS[mode];
	const $format = get(format);
	return entry ? $format(entry.key, { default: entry.default }) : String(mode);
};

/**
 * Build the label list for a Serial peripheral advanced card
 * (e.g. `"Modbus RTU Client"`) plus a compact line-settings summary
 * (e.g. `"115200 8N1"`) when baud + frame format are known.
 */
export const buildSerialPeripheralLabels = (
	peripheral: Peripheral,
): { icon?: string; value: string | number }[] => {
	const config = peripheral.settings as unknown as SerialConfig;
	const baud = config?.serial?.baud;
	const frameFormat = config?.serial?.format;
	const labels: { icon?: string; value: string | number }[] = [{ value: peripheral.value }];
	if (Number.isFinite(baud) && frameFormat) {
		labels.push({ value: `${baud} ${frameFormat}` });
	}
	return labels;
};

/**
 * Read the current `serial.mode` from the settings store (authoritative)
 * with a fallback to the live status payload. Returns `undefined` when
 * the component exists but the mode cannot be resolved.
 */
const readSerialMode = (
	settings: Record<string, any> | undefined,
	status: Record<string, any> | undefined,
	component: string,
): SerialMode | undefined => {
	const fromSettings = (settings?.[component] as SerialConfig | undefined)?.mode;
	if (fromSettings) return fromSettings;
	return (status?.[component] as { mode?: SerialMode } | undefined)?.mode;
};

/**
 * Returns a single addon `Peripheral` entry for the device's Serial
 * component, or leaves `interface_data` unchanged when:
 *   - the device has no `serial:N` component in its status, or
 *   - the serial mode is missing / `disabled` (functionally off).
 */
export const getSerialAddonStatus = (
	device: Device,
	interface_data: Peripheral[] = [],
): Peripheral[] => {
	const id = real_id(device?.id);
	const status = devices_status.getByRealId(id);
	const component = getSerialComponent(status);
	if (!component) return interface_data;

	const settings = readDeviceSettings(id);
	const mode = readSerialMode(settings, status, component);
	if (!mode || mode === 'disabled') return interface_data;

	const $format = get(format);
	const name = $format('label_serial_port_title', { default: 'Serial port' });
	const value = getSerialModeLabel(mode);
	const extid = Number(component.split(':')[1]);
	const serialSettings = settings?.[component] as SerialConfig | undefined;

	const sensor: Sensor = {
		id,
		type: 'serial',
		unit: '',
		component,
		inputType: mode,
		value,
		extid,
		alias: name,
		icon: SERIAL_ICON,
		label: name,
		name,
	};

	interface_data.push({
		name: component,
		peripheralType: 'serial',
		peripheralInputType: mode,
		icon: SERIAL_ICON,
		value,
		deviceID: id,
		sensor,
		settings: { ...(serialSettings ?? {}), id: extid } as unknown as PeripheralSettings,
	});

	return interface_data;
};
